import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useProject } from "@/hooks/useProjects";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, ExternalLink, Github, Calendar, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { formatDateNumeric } from "@/lib/date";
import { PROJETS } from "@/data/projets";
import { SeoSocial } from "@/components/SeoSocial";

const SITE_URL = "https://tokamdarius.ca";

const API_URL = import.meta.env.DEV
  ? // En developpement, on passe par le proxy Vite : le backend n'autorise en
    // CORS que localhost:8080, localhost:5173 et la production, donc un appel
    // direct depuis n'importe quel autre port est bloque par le navigateur.
    "/api"
  : "https://profile-sparkle-magic-production.up.railway.app/api";

interface Technology {
  id: number;
  name: string;
  slug: string;
  icon: string;
  color: string;
}

interface ProjectDetail {
  id: number;
  title: string;
  slug: string;
  short_description: string;
  description: string;
  project_type: string;
  project_type_display: string;
  status: string;
  cover_image: string;
  screenshots: string[];
  technologies: Technology[];
  live_url: string;
  github_url: string;
  demo_video_url: string;
  featured: boolean;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export default function ProjectDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  // React Query plutot que useEffect : le cache peut etre seme au pre-rendu,
  // donc cette page sort du build avec son contenu deja dans le HTML.
  const { project, loading, error } = useProject(slug || "");
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);
  // Les visuels locaux (rendus laptop et telephone, fond transparent) passent
  // devant les captures de l'API, qui viennent d'un service externe et se
  // regenerent a chaque visite. Le texte, lui, reste celui de l'API.
  const local = PROJETS.find((p) => p.slug === slug);
  const laptop = local?.laptop;

  if (loading) {
    return (
      <div className="relative z-10 min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center pt-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </main>
        <Footer />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="relative z-10 min-h-screen flex flex-col">
        <Helmet><title>{error ? "Projet momentanément indisponible" : "Projet introuvable"} | Tokam Darius</title>{!error && <meta name="robots" content="noindex, nofollow" />}</Helmet>
        <Header />
        <main className="flex-1 flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">{error ? "Projet momentanément indisponible" : "Projet non trouvé"}</h1>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button asChild>
              <Link to="/projets">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Retour aux projets
              </Link>
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const galerie =
    local?.galerie && local.galerie.length > 0
      ? local.galerie
      : (project.screenshots || []).map((src, i) => ({ src, alt: `${project.title}, capture ${i + 1}` }));

  return (
    <>
      <SeoSocial title={`${project.title} | Tokam Darius`} description={project.short_description} url={`${SITE_URL}/projets/${project.slug}`} image={new URL(project.cover_image || "/imagePartage.png", SITE_URL).href} />
      <Helmet>
        <title>{project.title} | Tokam Darius</title>
        <meta name="description" content={project.short_description} />
        <link rel="canonical" href={`${SITE_URL}/projets/${project.slug}`} />
        <meta property="og:url" content={`${SITE_URL}/projets/${project.slug}`} />
        <meta property="og:title" content={`${project.title} | Tokam Darius`} />
        <meta property="og:description" content={project.short_description} />
        <meta
          property="og:image"
          content={laptop ? `${SITE_URL}/projets/${laptop}-1400.webp` : project.cover_image}
        />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="relative z-10 min-h-screen flex flex-col">
        <Header />

        <main className="flex-1 pt-16">
          {laptop ? (
            /* Le rendu laptop, fond transparent, dans une boite a rapport fixe :
               l'espace est reserve avant le chargement, donc aucun saut de mise
               en page, et le contenu suit dans le flux au lieu de chevaucher. */
            <div className="container mx-auto px-4 pt-6 sm:px-6 sm:pt-10">
              <div className="relative mx-auto aspect-[2/1] max-w-5xl">
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-x-[12%] top-[16%] h-[58%] rounded-[50%]"
                  style={{
                    background:
                      "radial-gradient(ellipse, hsl(217 95% 39% / 0.32) 0%, transparent 70%)",
                    filter: "blur(48px)",
                  }}
                />
                <img
                  src={`/projets/${laptop}-1400.webp`}
                  srcSet={`/projets/${laptop}-900.webp 900w, /projets/${laptop}-1400.webp 1400w`}
                  sizes="(min-width: 1024px) 1024px, 100vw"
                  alt={`${project.title} sur ordinateur portable`}
                  width={1400}
                  height={700}
                  decoding="async"
                  // @ts-expect-error React 18 ne connait pas fetchPriority en camelCase
                  fetchpriority="high"
                  className="absolute inset-0 h-full w-full object-contain drop-shadow-[0_30px_60px_rgba(0,0,0,0.55)]"
                />
              </div>
            </div>
          ) : (
            /* Repli pour un projet absent de src/data/projets.ts : la capture
               de l'API en bandeau, comme avant. */
            <div className="relative h-64 md:h-80 overflow-hidden bg-gradient-to-br from-primary/20 via-primary/10 to-background">
              {project.cover_image && !imageError && (
                <img
                  src={project.cover_image}
                  alt={project.title}
                  className="w-full h-full object-cover"
                  onError={() => setImageError(true)}
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
            </div>
          )}

          {/* Content */}
          <div className={`container relative z-10 mx-auto px-4 sm:px-6 ${laptop ? "mt-8" : "-mt-20"}`}>
            <div className="max-w-4xl mx-auto">
              {/* Back button */}
              <div className="mb-4">
                <Button variant="outline" size="sm" asChild className="bg-background/80 backdrop-blur-sm">
                  <Link to="/projets">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Retour aux projets
                  </Link>
                </Button>
              </div>

              {/* Header Card */}
              <div className="bg-card rounded-lg border p-6 md:p-8 shadow-lg mb-8">
                <div className="flex flex-wrap gap-2 mb-4">
                  <Badge variant="default">{project.project_type_display}</Badge>
                  {project.featured && (
                    <Badge variant="secondary">En vedette</Badge>
                  )}
                </div>

                <h1 className="text-3xl md:text-4xl font-bold mb-4">{project.title}</h1>
                <p className="text-lg text-muted-foreground mb-6">{project.short_description}</p>

                {/* Action buttons */}
                <div className="flex flex-wrap gap-3">
                  {project.live_url && (
                    <Button asChild>
                      <a href={project.live_url} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="mr-2 h-4 w-4" />
                        Voir le site
                      </a>
                    </Button>
                  )}
                  {project.github_url && (
                    <Button variant="outline" asChild>
                      <a href={project.github_url} target="_blank" rel="noopener noreferrer">
                        <Github className="mr-2 h-4 w-4" />
                        Code source
                      </a>
                    </Button>
                  )}
                </div>
              </div>

              {/* Technologies */}
              <div data-reveal className="mb-8">
                <h2 className="text-xl font-semibold mb-4">Technologies</h2>
                <div className="flex flex-wrap gap-2">
                  {project.technologies.map((tech) => (
                    <Badge
                      key={tech.id}
                      variant="secondary"
                      className="text-sm py-1 px-3"
                      style={tech.color ? { borderColor: tech.color } : undefined}
                    >
                      {tech.name}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Video Demo */}
              {project.demo_video_url && (
                <div data-reveal className="mb-8">
                  <h2 className="text-xl font-semibold mb-4">Demo video</h2>
                  <div className="aspect-video rounded-lg overflow-hidden bg-muted">
                    {project.demo_video_url.includes("youtube.com") || project.demo_video_url.includes("youtu.be") ? (
                      <iframe
                        src={project.demo_video_url.replace("watch?v=", "embed/").replace("youtu.be/", "youtube.com/embed/")}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : project.demo_video_url.includes("loom.com") ? (
                      <iframe
                        src={project.demo_video_url.replace("/share/", "/embed/")}
                        className="w-full h-full"
                        allowFullScreen
                      />
                    ) : (
                      <video
                        src={project.demo_video_url}
                        controls
                        className="w-full h-full"
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Description */}
              <div data-reveal className="mb-8">
                <h2 className="text-xl font-semibold mb-4">A propos du projet</h2>
                <div className="prose prose-lg dark:prose-invert max-w-none">
                  <ReactMarkdown>{project.description}</ReactMarkdown>
                </div>
              </div>

              {/* Galerie : les rendus locaux d'abord, les captures de l'API sinon.
                  Un rendu a fond transparent se montre entier (object-contain),
                  jamais rogne : un telephone rogne en 16/9 n'a plus de sens. */}
              {galerie.length > 0 && (
                <div data-reveal className="mb-8">
                  <h2 className="text-xl font-semibold mb-4">Captures d'écran</h2>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {galerie.map((image) => (
                      <button
                        key={image.src}
                        type="button"
                        className="group relative aspect-video cursor-zoom-in overflow-hidden rounded-lg border border-white/10 bg-white/[0.03] p-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary active:scale-[0.97] transition-transform"
                        onClick={() => setSelectedImage(image.src)}
                        aria-label={`Agrandir : ${image.alt}`}
                      >
                        <img
                          src={image.src}
                          alt={image.alt}
                          className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-[1.03]"
                          loading="lazy"
                          decoding="async"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Dates */}
              {(project.started_at || project.completed_at) && (
                <div className="mb-8 flex items-center gap-4 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  {project.started_at && (
                    <span>Debut: {formatDateNumeric(project.started_at)}</span>
                  )}
                  {project.completed_at && (
                    <span>Fin: {formatDateNumeric(project.completed_at)}</span>
                  )}
                </div>
              )}

              {/* Back to projects */}
              <div className="py-8 border-t">
                <Button variant="outline" asChild>
                  <Link to="/projets">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Retour aux projets
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </main>

        <Footer />

        {/* Image Lightbox */}
        {selectedImage && (
          <div
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <button
              className="absolute top-4 right-4 text-white hover:text-gray-300 focus-visible:ring-2 active:scale-[0.97] transition-transform"
              aria-label="Fermer l'image agrandie"
              onClick={() => setSelectedImage(null)}
            >
              <X className="h-8 w-8" />
            </button>
            <img
              src={selectedImage}
              alt="Screenshot"
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </div>
    </>
  );
}
